/**
 * AutoSizingList.js
 *
 * @description  Defines a mixin for updating the size of a list as the window resizes.
 *
 * To use this, import it in a component and then add it as a mixin.
 *
  ```
    import { AutoSizingList } from '@/mixins'
    // ...
    mixins: [AutoSizingList],
  ```
 * In your template, you can then pass `size` and `remain`
 *
    <virtual-scroll-list
      :size='size'
      :remain='remain'
      />
 *
 * This can be even easier:
 *
 ```
   <virtual-scroll-list
     v-bind='this'
     />
 ```
 *
 */

export const AutoSizingList = {
  props: {
    /**
     * The amount of space to leave available for content above the list.
     */
    topInset: {
      type: Number,
      default: 0,
    },
    /**
     * The amount of space to leave available for content below the list.
     */
    bottomInset: {
      type: Number,
      default: 0,
    },
  },
  data () {
    return {
      windowHeight: window.innerHeight,
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    /**
     * Adds the top and bottom insets together.
     */
    verticalInsets () {
      return this.topInset + this.bottomInset
    },
    /**
     * This is the height of a cell if one is not specified.
     */
    defaultSize () {
      return 65.0
    },
    /**
     * * The number of cells that can fit onscreen.
     */
    remain () {
      const height = document.getElementById('owal-search-bar-container') ? document.getElementById('owal-search-bar-container').clientHeight : 44 // search bar is ~40px in height
      return (this.windowHeight - height) / this.size
    },
    /**
     * The height of a cell.
     * Override this in components that use this mixin.
     */
    size () {
      return this.defaultSize
    },
    // Backwards compatibility shim.
    /**
     * Returns the height of a cell.
     * This is an alias of size.
     */
    cellHeight () {
      return this.size
    },
    /**
     * The number of cells that can fit onscreen.
     * Alias of remain.
     */
    numberOfCells () {
      return this.remain
    },
  },
  methods: {
    handleResize () {
      this.windowHeight = window.innerHeight - (this.verticalInsets)
    },
  },
}
